import React from "react";

import type { CustomFieldsType } from "../../types/general";
import type { Language } from "../enums";

import { isStage } from "./environment";

export function splitArrayIntoChunks(array: Array<CustomFieldsType>, perChunk: number) {
  const numberOfChunks = Math.ceil(array.length / perChunk);
  const chunkedArray = [];

  let sliceCounter = 0;
  for (let i = 0; i < numberOfChunks; i++) {
    const chunk = array.slice(sliceCounter, sliceCounter + perChunk);
    chunkedArray.push({
      id: Math.random(),
      chunk,
    });
    sliceCounter += perChunk;
  }

  return chunkedArray;
}

export function splitTextIntoLines(text: string) {
  if (!text)
    return null;

  return text.split("\n").map((line, index) => (
    <React.Fragment key={line + index}>
      {line}
      {index < text.split("\n").length - 1 && <br />}
    </React.Fragment>
  ));
}

export function externalRedirectUrl(uuid: string, language: Language) {
  if (isStage()) {
    return `https://testing.innoscripta.com/${language}/thank-you?meetingId=${uuid}&booked=true`;
  }
  return `https://www.innoscripta.com/${language}/thank-you?meetingId=${uuid}&booked=true`;
}

/**
 * Extracts URL parameters and returns form data for auto-filling appointment forms
 *
 * Expected URL format: ?email={{Email}}&phone={{Phone}}&firstname={{FirstName}}&lastname={{LastName}}&gender={{Other}}&text={{Text}}
 *
 * @example
 * // URL: https://example.com/appointment?email=john@example.com&firstname=John&lastname=Doe&phone=1234567890&gender=male&text=Hello%20World
 * const params = extractUrlFormParams();
 * // Returns: { email: 'john@example.com', firstName: 'John', lastName: 'Doe', phone: '1234567890', gender: { label: 'Male', value: 'male' }, message: 'Hello World' }
 *
 * @returns Object containing form field values extracted from URL parameters
 */
export function extractUrlFormParams() {
  const urlParams = new URLSearchParams(window.location.search);

  const email = urlParams.get("email") || "";
  const phone = urlParams.get("phone") || "";
  const firstName = urlParams.get("firstname") || "";
  const lastName = urlParams.get("lastname") || "";
  const genderParam = urlParams.get("gender") || "";
  const message = urlParams.get("text") || "";

  // Map gender parameter to the expected format
  const getGenderOption = (genderValue: string) => {
    const normalizedGender = genderValue.toLowerCase();

    switch (normalizedGender) {
      case "male":
        return { label: "Male", value: "male" };
      case "female":
        return { label: "Female", value: "female" };
      case "other":
      default:
        return genderValue ? { label: "Other", value: "other" } : { label: "", value: "" };
    }
  };

  return {
    email,
    phone,
    firstName,
    lastName,
    gender: getGenderOption(genderParam),
    message,
  };
}
